import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ClassRoundedIcon from '@mui/icons-material/ClassRounded';
import VideoLibraryRoundedIcon from '@mui/icons-material/VideoLibraryRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import BookRoundedIcon from '@mui/icons-material/BookRounded';

import { AuthContext } from '../../auth/AuthContext';
import api from '../../api/api';
import AppShell from '../../layouts/AppShell';

const POLL_MS = 20000;
const NAV_LINKS = [
  { key: 'dashboard', label: 'Dashboard', path: '/student/dashboard', icon: DashboardRoundedIcon },
  {
    key: 'classes',
    label: 'My Timetable',
    path: '/student/classes',
    icon: ClassRoundedIcon,
    badgeKey: 'classes',
    dotKey: 'classes',
  },
  {
    key: 'recordings',
    label: 'Recordings',
    path: '/student/recordings',
    icon: VideoLibraryRoundedIcon,
  },
  {
    key: 'subjects',
    label: 'Subject Room',
    path: '/student/subjects',
    icon: BookRoundedIcon,
    dotKey: 'subjects',
  },
  {
    key: 'assessments',
    label: 'Assessments',
    path: '/student/assessments',
    icon: AssignmentTurnedInRoundedIcon,
    dotKey: 'assessments',
  },
  // Homework & Messages removed per new spec
  {
    key: 'announcements',
    label: 'Announcements',
    path: '/student/announcements',
    icon: CampaignRoundedIcon,
    dotKey: 'announcements',
  },
  {
    key: 'support',
    label: 'Support',
    path: '/student/support',
    icon: SupportAgentRoundedIcon,
  },
  {
    key: 'resources',
    label: 'Resources',
    path: '/student/resources',
    icon: BookRoundedIcon,
  },
  {
    key: 'settings',
    label: 'Settings',
    path: '/student/settings',
    icon: SettingsRoundedIcon,
  },
];

const StudentLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, setUser } = useContext(AuthContext) ?? {};

  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUpdatedAt, setAvatarUpdatedAt] = useState(null);

  useEffect(() => {
    const fetchCanonicalUser = async () => {
      if (!token || !user?._id) return;
      try {
        const { data } = await api.get(`/users/${user._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAvatarUrl(data?.profilePictureUrl || null);
        setAvatarUpdatedAt(data?.updatedAt || null);

        if (typeof setUser === 'function') {
          setUser((prev) => ({ ...(prev || {}), ...data }));
          try {
            localStorage.setItem('user', JSON.stringify({ ...(user || {}), ...data }));
          } catch {}
        }
      } catch {}
    };
    fetchCanonicalUser();
  }, [token, user?._id, setUser]);

  const displayName = user?.name || 'Student';

  const avatarSrc = useMemo(() => {
    const base = (avatarUrl || user?.profilePictureUrl || '').split('?')[0];
    const fallback = '/avatar.png';
    if (!base) return fallback;
    const stamp = avatarUpdatedAt
      ? new Date(avatarUpdatedAt).getTime()
      : user?.updatedAt
        ? new Date(user.updatedAt).getTime()
        : null;
    if (!stamp) return base;
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}v=${stamp}`;
  }, [avatarUrl, avatarUpdatedAt, user?.profilePictureUrl, user?.updatedAt]);

  const lsKey = useCallback(
    (k) => `lms:lastSeen:student:${k}:${user?._id || 'anon'}`,
    [user?._id]
  );

  const getLastSeen = useCallback(
   (k) => {
      try {
        return Number(localStorage.getItem(lsKey(k))) || 0;
      } catch {
        return 0;
      }
    },
    [lsKey]
  );

  const setLastSeen = useCallback(
   (k, t = Date.now()) => {
      try {
        localStorage.setItem(lsKey(k), String(t));
      } catch {}
    },
    [lsKey]
  );

  const [counts, setCounts] = useState({ classes: 0 });
  const [dots, setDots] = useState({
    announcements: false,
    classes: false,
  });

  useEffect(() => {
    if (!token) return;
    let intervalId;

    const countNewerThan = (items, lastSeen, timeKey = 'startTime') =>
      items.reduce((acc, item) => {
        const createdAt = item?.[timeKey] ? new Date(item[timeKey]).getTime() : 0;
        return createdAt > lastSeen ? acc + 1 : acc;
      }, 0);

    const fetchCounts = async () => {
      try {
        const { data } = await api.get('/sessions', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(data) ? data : [];
        const lastSeen = getLastSeen('classes');
        setCounts({
          classes: countNewerThan(list, lastSeen),
        });
      } catch {}
    };

    const start = () => {
      clearInterval(intervalId);
      fetchCounts();
      intervalId = setInterval(fetchCounts, POLL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(intervalId);
      } else {
        start();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    start();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [token, getLastSeen]);

  useEffect(() => {
    if (!location) return;

    if (location.pathname.startsWith('/student/announcements')) {
      setLastSeen('announcements');
      setDots((d) => ({ ...d, announcements: false }));
    }
    if (location.pathname.startsWith('/student/classes')) {
      setLastSeen('classes');
      setDots((d) => ({ ...d, classes: false }));
    }
  }, [location?.pathname, setLastSeen]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const checkAnnouncements = async () => {
      try {
        const { data } = await api.get('/notifications');
        const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const newest = list?.[0]?.createdAt
          ? new Date(list[0].createdAt).getTime()
          : 0;
        if (!cancelled && newest && newest > getLastSeen('announcements')) {
          setDots((d) => ({ ...d, announcements: true }));
        }
      } catch {}
    };
    const checkClasses = async () => {
      try {
        const { data } = await api.get('/sessions');
        const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const newest = list?.[0]?.startTime
          ? new Date(list[0].startTime).getTime()
          : 0;
        if (!cancelled && newest && newest > getLastSeen('classes')) {
          setDots((d) => ({ ...d, classes: true }));
        }
      } catch {}
    };

    const run = async () => {
      await Promise.all([checkAnnouncements(), checkClasses()]);
    };

    run();
    const id = setInterval(run, POLL_MS * 2);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, getLastSeen]);

  const navLinks = useMemo(
    () =>
      NAV_LINKS.map((link) => {
        const count = link.badgeKey ? counts[link.badgeKey] || 0 : 0;
        const dot = link.dotKey && dots[link.dotKey] && (link.badgeKey ? count === 0 : true);
        return { ...link, count, dot };
      }),
    [counts, dots]
  );

  const handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (typeof setUser === 'function') setUser(null);
    } catch {}
    navigate('/login');
  };

  const navItems = useMemo(
   () =>
      navLinks.map((link) => ({
        key: link.key,
        label: link.label,
        path: link.path,
        icon: <link.icon fontSize="medium" />,
        badgeCount: link.count,
        badgeDot: link.dot,
      })),
    [navLinks]
  );

  const headerTitle = (
    <Typography variant="h5" fontWeight={800} sx={{ color: 'primary.main' }}>
      Hi, {displayName}
    </Typography>
  );

  const topRight = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Avatar
        src={avatarSrc}
        alt={displayName}
        sx={{ width: 48, height: 48, border: '2px solid rgba(15,23,42,0.12)' }}
      />
      <Button
        variant="contained"
        color="primary"
        startIcon={<LogoutRoundedIcon />}
        onClick={handleLogout}
      >
        Logout
      </Button>
    </Stack>
  );

  return (
    <AppShell
      brand="Student"
      navItems={navItems}
      headerTitle={headerTitle}
      topRight={topRight}
      userName={displayName}
      userEmail={user?.email || ''}
      userRole="STUDENT"
      userAvatar={avatarSrc}
      collapseKey="studentSidebarCollapsed"
    >
      {children}
    </AppShell>
  );
};

export default StudentLayout;
